import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { reportRequestFailure, reportRequestSuccess } from "./server-status";

export function getCsrfToken(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)resonance_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, `${res.status}: ${text}`);
  }
}

function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof ApiError) {
    return error.status === 502 || error.status === 503 || error.status === 504;
  }
  return false;
}

export function adminFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const csrfToken = getCsrfToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
    ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
  };
  return fetch(url, { ...options, headers, credentials: "include" });
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const csrfToken = getCsrfToken();
  const headers: Record<string, string> = {
    ...(data ? { "Content-Type": "application/json" } : {}),
    ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
  };

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });
  } catch (err) {
    reportRequestFailure(0);
    throw err;
  }

  if (res.ok) {
    reportRequestSuccess();
  } else {
    reportRequestFailure(res.status);
  }

  await throwIfResNotOk(res);
  return res;
}

const ADMIN_FIELD_DENYLIST = new Set([
  "selectionTrace", "candidateConstraints", "discardedConstraints",
  "eliminationRounds", "winningCriterion", "validatorResults",
  "regenerationAttempts", "collapseRisk", "stabilityResult",
  "routeAuthoritySource", "auditLogger", "privateOutput",
  "rawPhaseOutput", "repairLog", "phaseOutputs", "overrideAuditTrail",
  "constraintSelection", "decisionState", "decisionStateAudit",
  "adminAuditData", "fullSynthesizedOutput", "selfCorrectionDetails",
  "hardlockDiagnostics", "compressionAudit", "scoringDetails",
  "monitoringSignal", "adaptiveIntelligence", "rawProtocolOutput",
]);

function stripAdminFieldsFromResponse(data: unknown): unknown {
  if (data === null || data === undefined || typeof data !== "object") return data;
  if (Array.isArray(data)) return data.map(stripAdminFieldsFromResponse);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (!ADMIN_FIELD_DENYLIST.has(key)) {
      cleaned[key] = typeof value === "object" ? stripAdminFieldsFromResponse(value) : value;
    }
  }
  return cleaned;
}

function isAdminPath(url: string): boolean {
  return url.startsWith("/api/admin") || url.startsWith("admin");
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;

    let res: Response;
    try {
      res = await fetch(url, { credentials: "include" });
    } catch (err) {
      reportRequestFailure(0);
      throw err;
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      reportRequestSuccess();
      return null;
    }

    if (res.ok) {
      reportRequestSuccess();
    } else {
      reportRequestFailure(res.status);
    }

    await throwIfResNotOk(res);
    const contentType = res.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const text = await res.text();
      throw new ApiError(res.status, `Expected JSON but received ${contentType || "unknown content type"}: ${text.slice(0, 100)}`);
    }
    const json = await res.json();
    if (isAdminPath(url)) {
      return json;
    }
    return stripAdminFieldsFromResponse(json);
  };

export const ADMIN_QUERY_KEY_PREFIX = "admin" as const;
export const PUBLIC_QUERY_KEY_PREFIX = "public" as const;

export function adminQueryKey(...segments: string[]): string[] {
  return [ADMIN_QUERY_KEY_PREFIX, ...segments];
}

export function publicQueryKey(...segments: string[]): string[] {
  return [PUBLIC_QUERY_KEY_PREFIX, ...segments];
}

export function invalidateAdminQueries() {
  queryClient.invalidateQueries({ queryKey: [ADMIN_QUERY_KEY_PREFIX] });
}

export function clearAdminCache() {
  queryClient.removeQueries({ queryKey: [ADMIN_QUERY_KEY_PREFIX] });
}

const BASE_RETRY_DELAY_MS = 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;
        return isRetryableError(error);
      },
      retryDelay: (attempt) => Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, 8000),
    },
    mutations: {
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;
        return isRetryableError(error);
      },
      retryDelay: (attempt) => Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, 8000),
    },
  },
});
